import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Generate } from './pages/Generate';
import { Video } from './pages/Video';
import { Landing } from './pages/Landing';
import { Games } from './pages/Games';
import { Dashboard } from './pages/Dashboard';
import { Terms, Privacy, Compliance2257, Contact } from './pages/Legal';
import { PaymentRules } from './pages/PaymentRules';
import { TakeItDownReport } from './pages/TakeItDownReport';
import { AppHeader } from './components/AppHeader';
import { LoginPopup } from './popups/LoginPopup';
import { PricingPopup } from './popups/PricingPopup';
import { AccountPopup } from './popups/AccountPopup';
import { AgeGatePopup } from './popups/AgeGatePopup';
import { getStorageValue } from '@genfun/core/utils';
import { fetchUser, logPageView, getSubscription } from '@genfun/core/api/auth';
import { GoogleOAuthProvider } from '@react-oauth/google';

const HEADER_PAGES = {
  landing: 'landing',
  photo: 'photo',
  video: 'video',
  games: 'games',
};

function MainApp() {
  // Read the token synchronously on first render (same JSON parse as getStorageValue)
  // so we already know the user is logged in before fetchUser resolves — avoids the
  // "Log in" button flashing for a split second before the account pill appears.
  const [token, setToken] = useState(() => {
    try {
      const saved = localStorage.getItem('igenfuntoken');
      return saved !== null ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [user, setUser] = useState(null);
  const [signedIn, setSignedIn] = useState(false);
  const [page, setPage] = useState('video');
  // media_id pinned to the report page when the user clicks "Report" under a
  // specific generation; cleared on any other navigation so it never goes stale.
  const [reportMediaId, setReportMediaId] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  // 18+ gate: skip for returning visitors (token) or those who already confirmed.
  const [ageConfirmed, setAgeConfirmed] = useState(() => {
    try {
      return localStorage.getItem('igenfuntoken') !== null
        || localStorage.getItem('age_confirmed') === 'true';
    } catch { return false; }
  });

  const gid = process.env.REACT_APP_GOOGLE_ID || '';

  useEffect(() => { if (user) setAgeConfirmed(true); }, [user]);

  const handleAgeConfirm = () => {
    try { localStorage.setItem('age_confirmed', 'true'); } catch {}
    setAgeConfirmed(true);
  };

  // Autoplay enabler (same as fuckable): muted autoplay needs a user gesture on
  // iOS / Low Power Mode. A persistent listener resumes any paused <video> on the
  // next tap/click; re-runs after login so videos start once the user is known.
  useEffect(() => {
    if (!ageConfirmed) return undefined;
    const playPaused = () => {
      document.querySelectorAll('video').forEach((v) => {
        if (v.paused) v.play().catch(() => {});
      });
    };
    playPaused();
    document.addEventListener('touchstart', playPaused, { passive: true });
    document.addEventListener('click', playPaused);
    return () => {
      document.removeEventListener('touchstart', playPaused);
      document.removeEventListener('click', playPaused);
    };
  }, [ageConfirmed, user]);

  useEffect(() => {
    (async () => {
      const t = getStorageValue("igenfuntoken", null, setToken);
      const [u, status] = await fetchUser(t);
      if (status === 401) {
        setToken(null);
        setUser(null);
      }
      setUser(u);
      if (t) {
        try {
          const [subData, subCode] = await getSubscription(t);
          setSubStatus(subCode === 200 && subData?.status ? subData.status : null);
        } catch (e) { setSubStatus(null); }
      } else {
        setSubStatus(null);
      }
    })();
  }, [signedIn]);

  const refreshUser = () => setSignedIn(s => !s);

  const navigate = (p) => { setReportMediaId(''); setPage(p); };
  const openReport = (mediaId) => { setReportMediaId(mediaId || ''); setPage('report'); };

  useEffect(() => {
    const refParam = new URLSearchParams(window.location.search).get('ref');
    if (refParam && !localStorage.getItem('referral_code')) {
      localStorage.setItem('referral_code', refParam);
    }
    if (!user) {
      const ref = refParam || localStorage.getItem('referral_code');
      logPageView(ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => {
    localStorage.removeItem('igenfuntoken');
    window.location.reload();
  };

  const pageProps = {
    token, user, setSignedIn,
    onNavigate: navigate,
    onReport: openReport,
    setShowLogin, setShowPricing,
    subStatus,
    onAccount: () => setShowAccount(true),
  };

  let content;
  if (page === 'landing') {
    content = <Landing {...pageProps} />;
  } else if (page === 'video') {
    content = <Video {...pageProps} />;
  } else if (page === 'games') {
    content = <Games {...pageProps} />;
  } else if (page === 'terms') {
    content = <Terms onNavigate={navigate} />;
  } else if (page === 'privacy') {
    content = <Privacy onNavigate={navigate} />;
  } else if (page === 'compliance') {
    content = <Compliance2257 onNavigate={navigate} />;
  } else if (page === 'contact') {
    content = <Contact onNavigate={navigate} />;
  } else if (page === 'payment') {
    content = <PaymentRules onNavigate={navigate} token={token} subStatus={subStatus} refreshUser={refreshUser} />;
  } else if (page === 'report') {
    content = <TakeItDownReport onNavigate={navigate} mediaId={reportMediaId} />;
  } else {
    content = <Generate {...pageProps} />;
  }

  const headerActive = HEADER_PAGES[page];

  const shell = (
    <>
      {headerActive && (
        <AppHeader
          active={headerActive}
          user={user}
          subStatus={subStatus}
          authPending={!!token && !user}
          onNavigate={navigate}
          onLogin={() => setShowLogin(true)}
          onAccount={() => (subStatus && subStatus !== 'active' ? setShowAccount(true) : setShowPricing(true))}
          onSignOut={signOut}
        />
      )}
      {content}
      <LoginPopup isOpen={showLogin} setIsOpen={setShowLogin} setSignedIn={setSignedIn} />
      <PricingPopup isOpen={showPricing} onClose={() => setShowPricing(false)} token={token} user={user} />
      <AccountPopup
        isOpen={showAccount}
        onClose={() => setShowAccount(false)}
        token={token}
        user={user}
        refreshUser={refreshUser}
      />
      {!ageConfirmed && <AgeGatePopup onConfirm={handleAgeConfirm} />}
    </>
  );

  return gid
    ? <GoogleOAuthProvider clientId={gid}>{shell}</GoogleOAuthProvider>
    : shell;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/refferal" element={<Dashboard />} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
